import { useMemo, useEffect } from "react";

import { Permissions } from "configs";
import { useAppSelector } from "stores";

const usePermission = (params?: { callback: (i: boolean) => void }) => {
  const { userProfile, currentOrganization } = useAppSelector(
    (state) => state.auth
  );

  const isSuperAdmin = useMemo(() => {
    return (
      userProfile?.isSuperAdmin || currentOrganization?.userAppRole === "Admin"
    );
  }, [currentOrganization?.userAppRole, userProfile?.isSuperAdmin]);

  const havePermission = useMemo(
    () => isSuperAdmin || Boolean(userProfile?.permissions?.length),
    [isSuperAdmin, userProfile?.permissions?.length]
  );

  useEffect(() => {
    if (params?.callback && typeof params?.callback === "function") {
      params?.callback(havePermission);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [havePermission]);

  const canAccess = (permissionFieldName: keyof typeof Permissions) => {
    const permission = Permissions[permissionFieldName];
    // by pass when permission has value "*"
    if ((permission as string) === "*") return true;
    if ((permission as string) === "SUPER_ADMIN") return isSuperAdmin;
    if (!userProfile) return false;

    return (
      isSuperAdmin || userProfile.permissions.some((p) => p === permission)
    );
  };

  const canAccessSome = (
    permissionFieldName: (keyof typeof Permissions)[] | keyof typeof Permissions
  ) => {
    if (!userProfile) return false;

    if (Array.isArray(permissionFieldName)) {
      return permissionFieldName.some((permission) => canAccess(permission));
    }
    return canAccess(permissionFieldName);
  };

  const canAccessEvery = (
    permissionFieldName: (keyof typeof Permissions)[] | keyof typeof Permissions
  ) => {
    if (!userProfile) return false;

    if (Array.isArray(permissionFieldName)) {
      return permissionFieldName.every((permission) => canAccess(permission));
    }
    return canAccess(permissionFieldName);
  };

  return {
    canAccessEvery,
    canAccessSome,
    canAccess,
    isSuperAdmin,
    havePermission,
  };
};

export default usePermission;
